import React, { useEffect, useState } from 'react';

import { useSpdCore } from '../../../common/hooks/useSpdCore';

import DictionarySelector from './DictionarySelector';
import { Option, Selector } from './Selector';

interface IDCardTypeSelectorProps {
  idCardTypeDictId?: string | null;
  iDCardType?: string | null;
  onChange?: (idCardTypeDictId: string | null, clearLegacy?: boolean) => void;
  readOnly?: boolean;
}

const DICT_TYPE = 'Rodzaj dokumentu tożsamości';

export const IDCardTypeSelector: React.FC<IDCardTypeSelectorProps> = ({
  idCardTypeDictId,
  iDCardType,
  onChange,
  readOnly,
}) => {
  const spd = useSpdCore();
  const [dictOptions, setDictOptions] = useState<Option[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    spd.dictionaries
      .getByDictionaryType(DICT_TYPE)
      .then(dicts => {
        setDictOptions(
          dicts
            .filter(d => d.active)
            .map(d => ({
              value: d.id ?? '',
              label: d.value,
              badge: d.shortname ?? undefined,
            })),
        );
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [spd.dictionaries]);

  useEffect(() => {
    setCleared(false);
  }, [iDCardType]);

  if (!loaded) {
    return null;
  }

  if (iDCardType) {
    const selectedDictOption = idCardTypeDictId
      ? dictOptions.find(o => o.value === idCardTypeDictId)
      : null;
    const currentValue = cleared
      ? undefined
      : selectedDictOption
        ? selectedDictOption.label
        : iDCardType;
    const currentBadge = cleared ? undefined : selectedDictOption?.badge;

    const isLegacyInDict = dictOptions.some(o => o.label === iDCardType);
    const allOptions = [
      ...(isLegacyInDict ? [] : [{ value: iDCardType!, label: iDCardType! }]),
      ...dictOptions,
    ];
    const provider = async (text: string) =>
      allOptions.filter(
        o =>
          o.label.toLowerCase().includes(text.toLowerCase()) ||
          (o.badge ?? '').toLowerCase().includes(text.toLowerCase()),
      );

    return (
      <Selector
        value={currentValue || undefined}
        valueBadge={currentBadge}
        provider={provider}
        onChange={option => {
          if (!option?.value) {
            setCleared(true);
            onChange?.(null, true);
            return;
          }
          setCleared(false);
          const isDict = dictOptions.some(o => o.value === option.value);
          if (isDict) {
            onChange?.(option.value);
          } else {
            onChange?.(null);
          }
        }}
        readOnly={readOnly}
        clearable={true}
      />
    );
  }

  return (
    <DictionarySelector
      value={idCardTypeDictId || ''}
      onChange={e => onChange?.(e?.value || null)}
      dictionary={DICT_TYPE}
      readOnly={readOnly}
      clearable={true}
      searchByShortname={true}
    />
  );
};
